/* global IDENTITY_SERVICE_ORIGIN, DEPLOYMENT_ENVIRONMENT, DD_LOGS_HANDLER */
import { datadogLogs } from '@datadog/browser-logs'

const REQUEST_TIMEOUT_MS = 10000
const PING_INTERVAL_MS = 50

const frameLogLevel = ['local', 'development'].includes(DEPLOYMENT_ENVIRONMENT) ? 'info' : 'warn'

const frameLogger = datadogLogs.createLogger('authFrameLogger', {
  level: frameLogLevel,
  handler: DD_LOGS_HANDLER || 'console',
  context: {
    domain: 'auth',
    component: 'frame-proxy',
  },
})

/**
 * Iframe-based auth proxy for cross-origin authentication requests.
 * Uses postMessage to communicate with an iframe hosted on the identity service
 * that can make requests with HttpOnly cookies.
 *
 * Available operations:
 * - 'auth:status' - GET /vendor/auth/status
 * - 'auth:login' - POST /vendor/auth/login
 * - 'auth:login-token' - POST /vendor/auth/login/token
 * - 'auth:token' - POST /vendor/auth/token
 * - 'auth:logout' - POST /vendor/auth/logout
 */
export class IframeAuthProxy {
  constructor() {
    this.iframe = document.getElementById('auth-proxy-frame')
    this.pendingRequests = new Map()
    this.timedOutRequestIds = new Set()
    this.requestId = 0

    this.pongReceived = false
    this.operationsReceived = false
    this.availableOperations = []
    this.ready = false

    window.addEventListener('message', this.handleMessage)

    this.pollIntervalId = setInterval(() => {
      if (!this.iframe?.contentWindow) {
        return
      }

      if (!this.pongReceived) {
        frameLogger.debug('[Vendor Proxy] Sending ping')
        this.iframe.contentWindow.postMessage({ type: 'proxy-ping' }, IDENTITY_SERVICE_ORIGIN)
      }

      if (!this.operationsReceived) {
        frameLogger.debug('[Vendor Proxy] Requesting operations')
        this.iframe.contentWindow.postMessage({ type: 'proxy-list-operations' }, IDENTITY_SERVICE_ORIGIN)
      }

      if (this.pongReceived && this.operationsReceived && !this.ready) {
        this.ready = true
        frameLogger.info('[Vendor Proxy] Auth proxy ready')
        clearInterval(this.pollIntervalId)
        this.pollIntervalId = null
      }
    }, PING_INTERVAL_MS)
  }

  handleMessage = (event) => {
    if (event.origin !== window.location.origin &&
      event.origin !== IDENTITY_SERVICE_ORIGIN) {
      return
    }

    const eventData = event.data
    if (!eventData || typeof eventData !== 'object' || !eventData.type) {
      return
    }

    if (eventData.type === 'proxy-pong') {
      frameLogger.info('[Vendor Proxy] Received proxy-pong')
      this.pongReceived = true
      return
    }

    if (eventData.type === 'proxy-operations') {
      frameLogger.info('[Vendor Proxy] Received available operations', { operations: eventData.operations })
      this.availableOperations = eventData.operations || []
      this.operationsReceived = true
      return
    }

    if (eventData.type !== 'proxy-response') {
      return
    }

    const pending = this.pendingRequests.get(eventData.id)
    if (!pending) {
      // Late response for a request we already timed out — the iframe's fetch
      // took longer than REQUEST_TIMEOUT_MS. Silently drop it; the caller has
      // already been rejected.
      if (this.timedOutRequestIds.delete(eventData.id)) {
        return
      }
      frameLogger.warn('[Vendor Proxy] Received response with no matching request', { id: eventData.id })
      return
    }

    this.pendingRequests.delete(eventData.id)
    pending.resolve({
      success: eventData.success,
      status: eventData.status,
      statusText: eventData.statusText,
      data: eventData.data,
      error: eventData.error,
    })
  }

  waitUntilReady() {
    if (this.ready) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const checkId = setInterval(() => {
        if (this.ready) {
          clearInterval(checkId)
          resolve()
        }
      }, PING_INTERVAL_MS)
    })
  }

  async operation(operation, options = {}) {
    const id = this.requestId++

    const responsePromise = new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject })
    })

    await this.waitUntilReady()
    frameLogger.debug('[Vendor Proxy] Sending request', { operation, id })
    this.iframe.contentWindow.postMessage({
      type: 'proxy-request',
      id,
      operation,
      body: options.body,
      headers: options.headers,
    }, IDENTITY_SERVICE_ORIGIN)

    const requestTimeoutId = setTimeout(() => {
      const pending = this.pendingRequests.get(id)
      if (pending) {
        this.pendingRequests.delete(id)
        this.timedOutRequestIds.add(id)
        pending.reject(new Error('Request timeout'))
      }
    }, REQUEST_TIMEOUT_MS)

    return responsePromise.finally(() => {
      clearTimeout(requestTimeoutId)
    })
  }

  destroy() {
    window.removeEventListener('message', this.handleMessage)
    if (this.pollIntervalId) {
      clearInterval(this.pollIntervalId)
      this.pollIntervalId = null
    }
    this.pendingRequests.clear()
    this.timedOutRequestIds.clear()
  }
}
